import { Banner } from '@evisort/eds/components/Banner/Banner';
import { Button } from '@evisort/eds/components/Button/Button';
import { FormField } from '@evisort/eds/components/FormField/FormField';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Text } from '@evisort/eds/components/Text/Text';
import { useForm, useStore } from '@tanstack/react-form';
import { type FormEvent, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { ForgotPasswordNavigationState } from '~/pages/login/types';

import { sendForgotPasswordEmail } from '~/api/login';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';
import { LoginLayout } from '~/pages/login/LoginLayout';
import { testIsEmailValid } from '~/pages/login/utils';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { getClientFromUrl } from '~/routing/utils';

import styles from './ForgotPasswordPage.module.css';

const messages = defineMessages({
  backToSignIn: {
    id: 'LoginPage.signInButton',
    defaultMessage: 'Sign In',
    description: 'Button to sign in',
  },
  checkInboxTitle: {
    id: 'LoginPage.checkInboxTitle',
    defaultMessage: 'Check your Inbox',
    description: 'Title for check inbox after password reset',
  },
  emailLabel: {
    id: 'LoginPage.emailLabel',
    defaultMessage: 'Email',
    description: 'Label for email input',
  },
  emailPlaceholder: {
    id: 'LoginPage.emailPlaceholder',
    defaultMessage: 'Type email address',
    description: 'Placeholder for email input',
  },
  errorMessage: {
    id: 'LoginPage.genericError',
    defaultMessage: 'An error occurred. Please check again.',
    description: 'Generic error message when domain check fails',
  },
  invalidEmailError: {
    id: 'LoginPage.invalidEmailError',
    defaultMessage: 'Enter a valid email address.',
    description: 'Validation error shown when the email address is malformed',
  },
  requiredFieldError: {
    id: 'LoginPage.requiredFieldError',
    defaultMessage: 'This field is required.',
    description: 'Validation error shown when a required field is empty',
  },
  resetLinkSentMessage: {
    id: 'LoginPage.resetLinkSentMessage',
    defaultMessage:
      'You will receive a reset link shortly. You may need to check your spam folder or unblock evisort@evisort.com.',
    description: 'Message shown after sending password reset link',
  },
  resetPasswordDescription: {
    id: 'LoginPage.resetPasswordDescription',
    defaultMessage:
      'Enter your email address to reset your password. You may need to check your spam folder or unblock evisort@evisort.com.',
    description: 'Description for reset password page',
  },
  resetPasswordTitle: {
    id: 'LoginPage.resetPasswordTitle',
    defaultMessage: 'Reset your Password',
    description: 'Title for reset password page',
  },
  sendResetLinkButton: {
    id: 'LoginPage.sendPasswordResetLinkButton',
    defaultMessage: 'Send Password Reset Link',
    description: 'Button to send password reset link',
  },
});

function Page() {
  const intl = useIntl();
  const { location, navigate } = useRouting();
  const navigationState = location.state as
    | ForgotPasswordNavigationState
    | undefined;
  const [isFetching, setIsFetching] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const form = useForm({
    defaultValues: { email: navigationState?.email ?? '' },
    onSubmit: async ({ value }) => {
      setIsFetching(true);
      setIsError(false);
      try {
        await sendForgotPasswordEmail({
          client: getClientFromUrl(),
          userId: value.email,
        });
        setIsSuccess(true);
      } catch {
        setIsError(true);
      } finally {
        setIsFetching(false);
      }
    },
  });

  useFormFocusManager(form);

  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );

  const validateEmail = ({ value }: { value: string }) => {
    if (!value.trim()) {
      return intl.formatMessage(messages.requiredFieldError);
    }
    return testIsEmailValid(value)
      ? undefined
      : intl.formatMessage(messages.invalidEmailError);
  };

  return (
    <LoginLayout>
      <Layout
        action="#"
        as="form"
        className={styles.form}
        direction="column"
        method="POST"
        noValidate
        spacing="l"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          form.handleSubmit().catch(() => {
            // Errors surface through `isError`.
          });
        }}
      >
        <Layout
          key={isSuccess ? 'success' : 'form'}
          className={styles.stageContent}
          direction="column"
          spacing="l"
        >
          <Layout direction="column" spacing="xs">
            <Text as="h1" variant="heading-xs">
              {isSuccess
                ? intl.formatMessage(messages.checkInboxTitle)
                : intl.formatMessage(messages.resetPasswordTitle)}
            </Text>
            {!isSuccess && (
              <Text as="p" tone="subdued" variant="body-m">
                {intl.formatMessage(messages.resetPasswordDescription)}
              </Text>
            )}
          </Layout>
          {isSuccess ? (
            <>
              <Banner
                status="positive"
                text={intl.formatMessage(messages.resetLinkSentMessage)}
              />
              <Layout className={styles.backAction} justify="center">
                <Button
                  icon={{ key: 'arrow-left', position: 'left' }}
                  label={intl.formatMessage(messages.backToSignIn)}
                  text={intl.formatMessage(messages.backToSignIn)}
                  variant="tertiary"
                  onClick={() =>
                    navigate(RoutePathType.Login, {
                      state: {
                        email: form.getFieldValue('email'),
                      } satisfies ForgotPasswordNavigationState,
                    })
                  }
                />
              </Layout>
            </>
          ) : (
            <>
              {isError && (
                <Banner
                  status="critical"
                  text={intl.formatMessage(messages.errorMessage)}
                />
              )}
              <form.Field
                name="email"
                validators={{
                  onChange: validateEmail,
                  onSubmit: validateEmail,
                }}
              >
                {(field) => (
                  <FormField
                    hint={
                      submissionAttempts > 0
                        ? (field.state.meta.errors[0] ?? undefined)
                        : undefined
                    }
                    label={intl.formatMessage(messages.emailLabel)}
                    name={field.name}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    required
                    status={
                      submissionAttempts > 0 && field.state.meta.errors[0]
                        ? 'critical'
                        : undefined
                    }
                    type="email"
                    value={field.state.value}
                    onChange={(next) => field.handleChange(next ?? '')}
                  />
                )}
              </form.Field>
              <Layout align="center" spacing="s">
                <Layout className={styles.backAction}>
                  <Button
                    icon={{ key: 'arrow-left', position: 'left' }}
                    label={intl.formatMessage(messages.backToSignIn)}
                    text={intl.formatMessage(messages.backToSignIn)}
                    variant="tertiary"
                    onClick={() =>
                      navigate(RoutePathType.Login, {
                        state: {
                          email: form.getFieldValue('email'),
                        } satisfies ForgotPasswordNavigationState,
                      })
                    }
                  />
                </Layout>
                <Layout className={styles.submitAction}>
                  <Button
                    isLoading={isFetching}
                    label={intl.formatMessage(messages.sendResetLinkButton)}
                    text={intl.formatMessage(messages.sendResetLinkButton)}
                    type="submit"
                    variant="primary"
                  />
                </Layout>
              </Layout>
            </>
          )}
        </Layout>
      </Layout>
    </LoginLayout>
  );
}

export default Page;
